<template>
  <Overlay
    :data-testid="dataTestId"
    :open="isOpen"
    :show-title="false"
    portal-target="#modal-overlay"
    layout="right/3"
    enter-from="right"
    :expanded="isExpanded"
    no-padding
    @update:open="() => close(emitWrapper)"
  >
    <form
      class="relative flex size-full flex-col"
      @submit="(event) => submit(event, emitWrapper)"
    >
      <TopNavigation
        show-back
        class="sticky top-0 z-[100] order-1 rounded-t-xl border-b border-shade-300 bg-white sm:rounded-none"
        @click:back="goToPreviousQuestion"
        @click:close="() => close(emitWrapper)"
      >
        {{ $t('contact_lens_consultant.title') }}
      </TopNavigation>
      <main
        class="h-full flex-1 px-6 pb-6 pt-12 transition-opacity"
        :class="[
          isExpanded
            ? 'pointer-events-none order-3 hidden opacity-0'
            : 'order-2 animate-appear flex-col',
        ]"
      >
        <!-- Question display -->
        <QuestionaireStep
          v-for="(question, index) in questions"
          :key="question._uid"
          :question="question"
          :payload="payload"
          :is-selected="isSelected"
          class="w-full"
          :class="{ hidden: index !== currentQuestionIndex }"
        />
      </main>
      <!-- No results screen -->
      <QuestionaireNoResult :visible="isExpanded && !hasResults" />
      <!-- Results screen -->
      <QuestionaireResult
        ref="resultScreen"
        :visible="isExpanded && hasResults"
        :items="items"
        @select:product="(item) => selectProduct(item, emitWrapper)"
      />

      <CustomerServiceBox
        class="order-4 mt-auto transition-opacity"
        :class="[
          isExpanded
            ? 'pointer-events-none hidden opacity-0'
            : 'animatuin-appear',
        ]"
      />

      <footer
        class="sticky bottom-0 z-[90] order-5 flex flex-col gap-3 bg-white @container"
      >
        <SimpleProcessBar
          :current="currentQuestionIndex"
          :length="questions.length"
          class="mt-auto transition-opacity"
          :class="{
            'pointer-events-none hidden': isExpanded,
            'animation-appear': !isExpanded,
          }"
        />

        <div
          v-if="!isExpanded"
          class="flex flex-col items-stretch px-4 pb-6 pt-0 sm:flex-row sm:items-center sm:justify-center"
        >
          <FimButton
            type="primary"
            class="w-full"
            :disabled="!canProceed || loading"
            :class="{ 'animate-pulse': loading }"
            html-type="submit"
          >
            {{
              isLastQuestion
                ? $t('contact_lens_consultant.show_results')
                : $t('contact_lens_consultant.proceed')
            }}
          </FimButton>
        </div>
        <QuestionaireCollapsible
          v-if="isExpanded"
          :payload="payload"
          :steps="questions"
          @click:edit="(index) => edit(index, emitWrapper)"
        >
          <template #cta>
            <FimButton
              v-if="hasResults"
              :data-testid="selectedProductId"
              type="primary"
              :to="localizedDetailLink"
              :disabled="!hasResults || !localizedDetailLink"
              @click="onCTAClick"
            >
              {{ $t('contact_lens_consultant.collabsible.cta') }}
            </FimButton>
            <FimButton
              v-else
              data-testid="Consultant | Appointment"
              type="primary"
              :to="localizedAppointmentLink"
              target="_blank"
              :disabled="!localizedAppointmentLink"
              @click="onButtonClick"
            >
              {{ $t('contact_lens_consultant.collabsible.book_appointment') }}
            </FimButton>
          </template>
          <template #action>
            <FimButton
              data-testid="Consultant | Restart"
              type="link"
              class="border-0! ml-0"
              no-padding
              @click="(event: MouseEvent) => restart(event, emitWrapper)"
            >
              {{ $t('contact_lens_consultant.collabsible.restart') }}
            </FimButton>
            <FimButton
              v-if="hasResults"
              :to="localizedAppointmentLink"
              data-testid="Consultant | Appointment"
              type="link"
              class="border-0! ml-0"
              no-padding
              target="_blank"
              :disabled="!localizedAppointmentLink"
              :class="{
                'sm:hidden': !localizedAppointmentLink,
              }"
              @click="onButtonClick"
            >
              {{ $t('contact_lens_consultant.collabsible.book_appointment') }}
            </FimButton>
          </template>
        </QuestionaireCollapsible>
      </footer>
    </form>
  </Overlay>
</template>

<script setup lang="ts">
import type { SbQuestionnaire } from '~/storyblok/types/storyblok'
import { useQuestionnaire } from '~/storyblok/composables/useQuestionnaire'
import QuestionaireCollapsible from './QuestionaireCollapsible.vue'
import QuestionaireNoResult from './QuestionaireNoResult.vue'
import QuestionaireResult from './QuestionaireResult.vue'
import QuestionaireStep from './QuestionaireStep.vue'

const { questionnaireProps, dataTestId } = defineProps<{
  questionnaireProps: SbQuestionnaire
  dataTestId: string
}>()

const emit = defineEmits<{
  (e: 'click:close' | 'click:open' | 'restart'): void
  (e: 'edit' | 'show:result' | 'select:product', params: any): void
}>()

// Create wrapper function that matches EmitFunction type from useQuestionnaire
const emitWrapper = (event: string, payload?: unknown) => {
  if (
    event === 'click:close' ||
    event === 'click:open' ||
    event === 'restart'
  ) {
    emit(event as 'click:close' | 'click:open' | 'restart')
  } else if (
    event === 'edit' ||
    event === 'show:result' ||
    event === 'select:product'
  ) {
    emit(event as 'edit' | 'show:result' | 'select:product', payload)
  }
}

const {
  isOpen,
  isExpanded,
  loading,
  items,
  currentQuestionIndex,
  resultScreen,
  hasResults,
  questions,
  payload,
  isLastQuestion,
  canProceed,
  selectedProductId,
  localizedDetailLink,
  localizedAppointmentLink,
  isSelected,
  close,
  open,
  restart,
  edit,
  selectProduct,
  goToPreviousQuestion,
  submit,
  onButtonClick,
  onCTAClick,
} = useQuestionnaire(questionnaireProps)

// Expose methods for parent components
defineExpose({
  open: (event: MouseEvent) => open(event, emitWrapper),
  close: () => close(emitWrapper),
  restart: (event: MouseEvent) => restart(event, emitWrapper),
  resultScreen,
})
</script>
